import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";

class Notifications extends Component {
  state = {
    notifications: [],
    formData: {
      id: null,
      notification_title: "",
      notification_message: "",
      notification_type: "",
      notification_status: "1",
      notification_date: "",
    },
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    modalMode: "add",
  };

  componentDidMount() {
    this.fetchNotifications();

    const modalEl = document.getElementById("notificationModal");
    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", () => {
        this.resetForm();
      });
    }
  }

  /** ✅ Fetch all notifications */
  fetchNotifications = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/notifications", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data?.data || [];
      this.setState({ notifications: Array.isArray(data) ? data : [] });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message ||
          "Failed to load notifications. Please try again.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /** ✅ Reset form */
  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        notification_title: "",
        notification_message: "",
        notification_type: "",
        notification_status: "1",
        notification_date: "",
      },
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  /** ✅ Input change */
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: {
        ...prev.formData,
        [name]: value,
      },
    }));
  };

  /** ✅ Open modal */
  openModal = (mode = "add", notification = null) => {
    const modal = new window.bootstrap.Modal(
      document.getElementById("notificationModal")
    );

    if (mode === "edit" && notification) {
      this.setState({
        modalMode: "edit",
        formData: {
          id: notification.id,
          notification_title: notification.notification_title || "",
          notification_message: notification.notification_message || "",
          notification_type: notification.notification_type || "",
          notification_status:
            notification.notification_status?.toString() ?? "1",
          notification_date: notification.notification_date || "",
        },
        error: "",
      });
    } else {
      this.resetForm();
    }

    modal.show();
  };

  /** ✅ Submit add/update */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.notification_title) {
      return this.setState({ error: "Notification title is required." });
    }
    if (!formData.notification_message) {
      return this.setState({ error: "Notification message is required." });
    }
    if (formData.notification_status === "") {
      return this.setState({ error: "Status is required." });
    }

    const data = new FormData();
    data.append("notification_title", formData.notification_title);
    data.append("notification_message", formData.notification_message);
    data.append("notification_type", formData.notification_type);
    data.append("notification_status", formData.notification_status);
    data.append("notification_date", formData.notification_date);

    this.setState({ submitting: true, error: "" });

    try {
      const token = localStorage.getItem("token");

      if (modalMode === "add") {
        await axios.post("/notifications", data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        await axios.post(`/notifications/${formData.id}?_method=PUT`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      await this.fetchNotifications();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("notificationModal")
      );
      if (modal) modal.hide();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "Notification added successfully!"
            : "Notification updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving notification.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  /** ✅ Confirm delete */
  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modal = new window.bootstrap.Modal(
      document.getElementById("deleteModal")
    );
    modal.show();
  };

  /** ✅ Delete notification */
  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/notifications/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await this.fetchNotifications();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("deleteModal")
      );
      if (modal) modal.hide();

      Swal.fire({
        icon: "success",
        title: "Notification deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to delete notification.",
      });
    }
  };

  render() {
    const {
      notifications,
      formData,
      error,
      loading,
      submitting,
      modalMode,
    } = this.state;

    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="row">
            <div className="col-lg-12">
              <div className="card shadow-none">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <ul className="breadcrumb mb-0">
                    <li className="breadcrumb-item">Notifications</li>
                    <li className="breadcrumb-item active">List</li>
                  </ul>

                  <button
                    type="button"
                    className="btn btn-light-warning"
                    onClick={() => this.openModal("add")}
                  >
                    + New Notification
                  </button>
                </div>

                <div className="card-body">
                  {error && <div className="alert alert-danger">{error}</div>}

                  {loading ? (
                    <div className="text-center py-4">Loading...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Title</th>
                            <th>Message</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {notifications.length > 0 ? (
                            notifications.map((notification, index) => (
                              <tr key={notification.id}>
                                <td>{index + 1}</td>
                                <td>{notification.notification_title}</td>
                                <td>{notification.notification_message}</td>
                                <td>{notification.notification_type || "N/A"}</td>
                                <td>
                                  {notification.notification_status === "1" ||
                                  notification.notification_status === 1 ? (
                                    <span className="badge bg-success">
                                      Active
                                    </span>
                                  ) : (
                                    <span className="badge bg-danger">
                                      Inactive
                                    </span>
                                  )}
                                </td>
                                <td>{notification.notification_date || "N/A"}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-success me-2"
                                    onClick={() =>
                                      this.openModal("edit", notification)
                                    }
                                  >
                                    <i className="ph ph-note-pencil"></i>
                                  </button>

                                  <button
                                    className="btn btn-sm btn-light-danger"
                                    onClick={() =>
                                      this.confirmDelete(notification.id)
                                    }
                                  >
                                    <i className="ph ph-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">
                                No notifications found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add / Edit Modal */}
        <div className="modal fade" id="notificationModal" tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalMode === "add"
                      ? "Add Notification"
                      : "Edit Notification"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    {/* Notification Title */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Notification Title</label>
                      <input
                        type="text"
                        name="notification_title"
                        className="form-control"
                        value={formData.notification_title}
                        onChange={this.handleChange}
                        required
                      />
                    </div>

                    {/* Notification Type */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Notification Type</label>
                      <input
                        type="text"
                        name="notification_type"
                        className="form-control"
                        value={formData.notification_type}
                        onChange={this.handleChange}
                        placeholder="e.g. info / success / warning"
                      />
                    </div>

                    {/* Notification Date */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Notification Date</label>
                      <input
                        type="date"
                        name="notification_date"
                        className="form-control"
                        value={formData.notification_date}
                        onChange={this.handleChange}
                      />
                    </div>

                    {/* Notification Status */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status</label>
                      <select
                        name="notification_status"
                        className="form-select"
                        value={formData.notification_status}
                        onChange={this.handleChange}
                        required
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </div>

                    {/* Notification Message */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Notification Message</label>
                      <textarea
                        name="notification_message"
                        className="form-control"
                        rows="5"
                        value={formData.notification_message}
                        onChange={this.handleChange}
                        required
                      ></textarea>
                    </div>

                    {error && (
                      <div className="col-md-12">
                        <div className="alert alert-danger">{error}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-light-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Saving..."
                      : modalMode === "add"
                      ? "Add"
                      : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-body text-center p-4">
                <i className="ph ph-warning text-danger f-40 mb-3"></i>
                <h5>Are you sure?</h5>
                <p className="text-muted mb-4">This action cannot be undone.</p>

                <div className="d-flex justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-light-danger"
                    onClick={this.handleDelete}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Notifications;
